
import { useState, useEffect } from 'react';
import { supabase } from '../../../../lib/supabase';

interface Application {
  id: string;
  full_name: string;
  email: string;
  phone: string;
  location: string;
  current_occupation: string;
  is_qualified: boolean;
  why_earn: string;
  why_selected: string;
  open_to_mentorship: string;
  commit_time: string;
  follow_structure: string;
  referred_by: string;
  heard_from: string;
  status: string;
  created_at: string;
  notes: string | null;
}

export default function ApplicationsTab() {
  const [applications, setApplications] = useState<Application[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'qualified' | 'not-qualified'>('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Application | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkDeleteTarget, setBulkDeleteTarget] = useState<string[]>([]);
  const [savingNotes, setSavingNotes] = useState<string | null>(null);

  useEffect(() => {
    fetchApplications();
  }, []);

  useEffect(() => {
    setSelectedIds(new Set());
  }, [searchQuery, statusFilter, dateFrom, dateTo]);

  const fetchApplications = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('applications')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching applications:', error);
    } else {
      setApplications(data || []);
    }
    setLoading(false);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    const { error } = await supabase
      .from('applications')
      .delete()
      .eq('id', deleteTarget.id);

    if (error) {
      console.error('Error deleting application:', error);
    } else {
      setApplications(prev => prev.filter(a => a.id !== deleteTarget.id));
    }
    setDeleting(false);
    setDeleteTarget(null);
  };

  const handleBulkDelete = async () => {
    if (bulkDeleteTarget.length === 0) return;
    setDeleting(true);
    const { error } = await supabase
      .from('applications')
      .delete()
      .in('id', bulkDeleteTarget);

    if (error) {
      console.error('Error bulk deleting applications:', error);
    } else {
      setApplications(prev => prev.filter(a => !bulkDeleteTarget.includes(a.id)));
      setSelectedIds(new Set());
    }
    setDeleting(false);
    setBulkDeleteTarget([]);
  };

  const updateNotes = async (id: string, notes: string) => {
    setSavingNotes(id);
    const { error } = await supabase
      .from('applications')
      .update({ notes })
      .eq('id', id);

    if (error) {
      console.error('Error updating notes:', error);
    } else {
      setApplications(prev =>
        prev.map(app => (app.id === id ? { ...app, notes } : app))
      );
    }
    setSavingNotes(null);
  };

  const clearDateRange = () => {
    setDateFrom('');
    setDateTo('');
  };

  const filteredApplications = applications.filter(app => {
    const matchesSearch =
      app.full_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      app.email.toLowerCase().includes(searchQuery.toLowerCase());

    const matchesStatus =
      statusFilter === 'all' ||
      (statusFilter === 'qualified' && app.is_qualified) ||
      (statusFilter === 'not-qualified' && !app.is_qualified);

    const appDate = new Date(app.created_at);
    const matchesFrom = !dateFrom || appDate >= new Date(dateFrom + 'T00:00:00');
    const matchesTo = !dateTo || appDate <= new Date(dateTo + 'T23:59:59');

    return matchesSearch && matchesStatus && matchesFrom && matchesTo;
  });

  const toggleExpand = (id: string) => {
    setExpandedId(prev => (prev === id ? null : id));
  };

  const toggleSelect = (id: string) => {
    setSelectedIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === filteredApplications.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredApplications.map(app => app.id)));
    }
  };

  const exportCSV = () => {
    const toExport = selectedIds.size > 0
      ? filteredApplications.filter(app => selectedIds.has(app.id))
      : filteredApplications;

    const headers = [
      'Full Name', 'Email', 'Phone', 'Location', 'Occupation',
      'Qualified', 'Status', 'Referred By', 'Heard From', 'Submitted Date', 'Notes',
    ];

    const rows = toExport.map(app => [
      app.full_name,
      app.email,
      app.phone,
      app.location,
      app.current_occupation,
      app.is_qualified ? 'Yes' : 'No',
      app.status,
      app.referred_by,
      app.heard_from,
      new Date(app.created_at).toLocaleDateString(),
      app.notes || '',
    ]);

    const csvContent = [headers, ...rows]
      .map(row => row.map(cell => `"${cell}"`).join(','))
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `applications_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Delete Confirmation Modal */}
      {deleteTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6 space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                <i className="ri-delete-bin-line text-red-600 text-lg"></i>
              </div>
              <div>
                <h3 className="font-semibold text-stone-900 text-base">Delete Application</h3>
                <p className="text-sm text-stone-500">This action cannot be undone.</p>
              </div>
            </div>
            <p className="text-sm text-stone-700">
              Are you sure you want to delete the application from{' '}
              <strong>{deleteTarget.full_name}</strong> ({deleteTarget.email})?
            </p>
            <div className="flex gap-3 pt-2">
              <button
                onClick={() => setDeleteTarget(null)}
                disabled={deleting}
                className="flex-1 px-4 py-2 border border-stone-300 text-stone-700 rounded-lg hover:bg-stone-50 transition-colors text-sm font-medium whitespace-nowrap cursor-pointer"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={deleting}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm font-medium whitespace-nowrap flex items-center justify-center gap-2 cursor-pointer"
              >
                {deleting ? (
                  <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div> Deleting...</>
                ) : (
                  <><i className="ri-delete-bin-line"></i> Delete</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Delete Confirmation Modal */}
      {bulkDeleteTarget.length > 0 && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6 space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                <i className="ri-delete-bin-line text-red-600 text-lg"></i>
              </div>
              <div>
                <h3 className="font-semibold text-stone-900 text-base">Delete Multiple Applications</h3>
                <p className="text-sm text-stone-500">This action cannot be undone.</p>
              </div>
            </div>
            <p className="text-sm text-stone-700">
              Are you sure you want to delete <strong>{bulkDeleteTarget.length}</strong> application{bulkDeleteTarget.length !== 1 ? 's' : ''}?
            </p>
            <div className="flex gap-3 pt-2">
              <button
                onClick={() => setBulkDeleteTarget([])}
                disabled={deleting}
                className="flex-1 px-4 py-2 border border-stone-300 text-stone-700 rounded-lg hover:bg-stone-50 transition-colors text-sm font-medium whitespace-nowrap cursor-pointer"
              >
                Cancel
              </button>
              <button
                onClick={handleBulkDelete}
                disabled={deleting}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm font-medium whitespace-nowrap flex items-center justify-center gap-2 cursor-pointer"
              >
                {deleting ? (
                  <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div> Deleting...</>
                ) : (
                  <><i className="ri-delete-bin-line"></i> Delete All</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toolbar Row 1: Search + Status + Export */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="flex-1">
          <input
            type="text"
            placeholder="Search by name or email..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            className="w-full px-4 py-2 border border-stone-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 text-sm"
          />
        </div>
        <select
          value={statusFilter}
          onChange={e => setStatusFilter(e.target.value as 'all' | 'qualified' | 'not-qualified')}
          className="px-4 py-2 border border-stone-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 text-sm whitespace-nowrap cursor-pointer"
        >
          <option value="all">All Status</option>
          <option value="qualified">Qualified Only</option>
          <option value="not-qualified">Not Qualified Only</option>
        </select>
        <button
          onClick={exportCSV}
          className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors text-sm whitespace-nowrap cursor-pointer"
        >
          {selectedIds.size > 0 ? `Export Selected (${selectedIds.size})` : 'Export CSV'}
        </button>
      </div>

      {/* Toolbar Row 2: Date Range Filter */}
      <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center">
        <div className="flex items-center gap-2 flex-shrink-0">
          <i className="ri-calendar-line text-stone-400"></i>
          <span className="text-sm text-stone-600 font-medium whitespace-nowrap">Date Range:</span>
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          <div className="flex items-center gap-2">
            <label className="text-xs text-stone-500 whitespace-nowrap">From</label>
            <input
              type="date"
              value={dateFrom}
              onChange={e => setDateFrom(e.target.value)}
              className="px-3 py-2 border border-stone-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 text-sm cursor-pointer"
            />
          </div>
          <div className="flex items-center gap-2">
            <label className="text-xs text-stone-500 whitespace-nowrap">To</label>
            <input
              type="date"
              value={dateTo}
              min={dateFrom}
              onChange={e => setDateTo(e.target.value)}
              className="px-3 py-2 border border-stone-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 text-sm cursor-pointer"
            />
          </div>
          {(dateFrom || dateTo) && (
            <button
              onClick={clearDateRange}
              className="px-3 py-2 bg-stone-200 text-stone-700 rounded-lg hover:bg-stone-300 transition-colors text-sm whitespace-nowrap cursor-pointer flex items-center gap-1"
            >
              <i className="ri-close-line"></i> Clear
            </button>
          )}
        </div>
        {(dateFrom || dateTo) && (
          <span className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 px-3 py-1.5 rounded-full font-medium whitespace-nowrap">
            {filteredApplications.length} result{filteredApplications.length !== 1 ? 's' : ''} in range
          </span>
        )}
      </div>

      {/* Bulk Action Bar */}
      {selectedIds.size > 0 && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-lg px-4 py-3 flex items-center justify-between">
          <p className="text-emerald-800 font-medium text-sm">
            {selectedIds.size} application{selectedIds.size !== 1 ? 's' : ''} selected
          </p>
          <button
            onClick={() => setBulkDeleteTarget(Array.from(selectedIds))}
            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm font-medium whitespace-nowrap flex items-center gap-2 cursor-pointer"
          >
            <i className="ri-delete-bin-line"></i> Delete Selected
          </button>
        </div>
      )}

      {/* Desktop Table */}
      <div className="hidden lg:block overflow-x-auto bg-white rounded-lg border border-stone-200">
        <table className="w-full">
          <thead className="bg-stone-50 border-b border-stone-200">
            <tr>
              <th className="px-4 py-3 text-left">
                <input
                  type="checkbox"
                  checked={filteredApplications.length > 0 && selectedIds.size === filteredApplications.length}
                  onChange={toggleSelectAll}
                  className="w-4 h-4 text-emerald-600 border-stone-300 rounded focus:ring-emerald-500 cursor-pointer"
                />
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-stone-700 uppercase tracking-wider">Full Name</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-stone-700 uppercase tracking-wider">Email</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-stone-700 uppercase tracking-wider">Phone</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-stone-700 uppercase tracking-wider">Location</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-stone-700 uppercase tracking-wider">Occupation</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-stone-700 uppercase tracking-wider">Qualified</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-stone-700 uppercase tracking-wider">Submitted</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-stone-700 uppercase tracking-wider">Details</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-stone-700 uppercase tracking-wider">Action</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-stone-200">
            {filteredApplications.map(app => (
              <>
                <tr key={app.id} className="hover:bg-stone-50 transition-colors">
                  <td className="px-4 py-3">
                    <input
                      type="checkbox"
                      checked={selectedIds.has(app.id)}
                      onChange={() => toggleSelect(app.id)}
                      className="w-4 h-4 text-emerald-600 border-stone-300 rounded focus:ring-emerald-500 cursor-pointer"
                    />
                  </td>
                  <td className="px-4 py-3 text-sm font-medium text-stone-900">{app.full_name}</td>
                  <td className="px-4 py-3 text-sm text-stone-600">{app.email}</td>
                  <td className="px-4 py-3 text-sm text-stone-600">{app.phone}</td>
                  <td className="px-4 py-3 text-sm text-stone-600">{app.location}</td>
                  <td className="px-4 py-3 text-sm text-stone-600">{app.current_occupation}</td>
                  <td className="px-4 py-3 text-sm">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      app.is_qualified
                        ? 'bg-emerald-100 text-emerald-800'
                        : 'bg-red-100 text-red-700'
                    }`}>
                      {app.is_qualified ? 'Yes' : 'No'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-stone-600">
                    {new Date(app.created_at).toLocaleDateString()}
                  </td>
                  <td className="px-4 py-3 text-sm">
                    <button
                      onClick={() => toggleExpand(app.id)}
                      className="flex items-center gap-1 text-emerald-600 hover:text-emerald-700 font-medium text-xs cursor-pointer"
                    >
                      {expandedId === app.id ? (
                        <>Hide <i className="ri-arrow-up-s-line"></i></>
                      ) : (
                        <>View <i className="ri-arrow-down-s-line"></i></>
                      )}
                    </button>
                  </td>
                  <td className="px-4 py-3 text-sm">
                    <button
                      onClick={() => setDeleteTarget(app)}
                      className="flex items-center gap-1 text-red-500 hover:text-red-700 font-medium text-xs cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-delete-bin-line"></i> Delete
                    </button>
                  </td>
                </tr>

                {expandedId === app.id && (
                  <tr key={`${app.id}-details`}>
                    <td colSpan={10} className="px-4 py-4 bg-emerald-50 border-l-4 border-l-emerald-500">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm mb-4">
                        <DetailItem label="Why Interested" value={app.why_earn} />
                        <DetailItem label="Why Should Be Selected" value={app.why_selected} />
                        <DetailItem label="Open to Mentorship" value={app.open_to_mentorship} />
                        <DetailItem label="Willing to Commit Time" value={app.commit_time} />
                        <DetailItem label="Follow Structure" value={app.follow_structure} />
                        <DetailItem label="Referred By" value={app.referred_by} />
                        <DetailItem label="Heard From" value={app.heard_from} />
                        <DetailItem label="Application Status" value={app.status} />
                      </div>
                      {/* Notes Field */}
                      <div className="space-y-1 pt-3 border-t border-emerald-200">
                        <label className="text-xs font-semibold text-stone-600 uppercase tracking-wider flex items-center gap-1">
                          <i className="ri-sticky-note-line"></i> Internal Notes
                        </label>
                        <textarea
                          value={app.notes || ''}
                          onChange={e =>
                            setApplications(prev =>
                              prev.map(a => (a.id === app.id ? { ...a, notes: e.target.value } : a))
                            )
                          }
                          onBlur={e => updateNotes(app.id, e.target.value)}
                          placeholder="Add private notes or comments about this applicant..."
                          rows={3}
                          className="w-full px-3 py-2 border border-stone-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 text-sm resize-none bg-white"
                          disabled={savingNotes === app.id}
                        />
                        {savingNotes === app.id && (
                          <p className="text-xs text-emerald-600 flex items-center gap-1">
                            <div className="w-3 h-3 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin"></div>
                            Saving...
                          </p>
                        )}
                      </div>
                    </td>
                  </tr>
                )}
              </>
            ))}
          </tbody>
        </table>
        {filteredApplications.length === 0 && (
          <div className="text-center py-12 text-stone-500">
            No applications found matching your search.
          </div>
        )}
      </div>

      {/* Mobile Cards */}
      <div className="lg:hidden space-y-4">
        {filteredApplications.map(app => (
          <div key={app.id} className="bg-white rounded-lg border border-stone-200 p-4 space-y-3">
            <div className="flex items-start gap-3">
              <input
                type="checkbox"
                checked={selectedIds.has(app.id)}
                onChange={() => toggleSelect(app.id)}
                className="w-5 h-5 text-emerald-600 border-stone-300 rounded focus:ring-emerald-500 cursor-pointer mt-1 flex-shrink-0"
              />
              <div className="flex-1">
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="font-semibold text-stone-900">{app.full_name}</h3>
                    <p className="text-sm text-stone-600">{app.email}</p>
                  </div>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    app.is_qualified
                      ? 'bg-emerald-100 text-emerald-800'
                      : 'bg-red-100 text-red-700'
                  }`}>
                    {app.is_qualified ? 'Qualified' : 'Not Qualified'}
                  </span>
                </div>

                <div className="space-y-1 text-sm text-stone-600 mt-3">
                  <div className="flex items-center gap-2">
                    <i className="ri-phone-line text-stone-400"></i>
                    <span>{app.phone}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <i className="ri-map-pin-line text-stone-400"></i>
                    <span>{app.location}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <i className="ri-briefcase-line text-stone-400"></i>
                    <span>{app.current_occupation}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <i className="ri-calendar-line text-stone-400"></i>
                    <span>{new Date(app.created_at).toLocaleDateString()}</span>
                  </div>
                </div>

                <div className="flex gap-2 mt-3">
                  <button
                    onClick={() => toggleExpand(app.id)}
                    className="flex-1 flex items-center justify-center gap-1 py-2 border border-emerald-300 text-emerald-700 rounded-lg text-sm font-medium hover:bg-emerald-50 transition-colors cursor-pointer"
                  >
                    {expandedId === app.id ? (
                      <>Hide Details <i className="ri-arrow-up-s-line"></i></>
                    ) : (
                      <>View Details <i className="ri-arrow-down-s-line"></i></>
                    )}
                  </button>
                  <button
                    onClick={() => setDeleteTarget(app)}
                    className="px-4 py-2 border border-red-300 text-red-600 rounded-lg text-sm font-medium hover:bg-red-50 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1"
                  >
                    <i className="ri-delete-bin-line"></i> Delete
                  </button>
                </div>

                {expandedId === app.id && (
                  <div className="pt-3 border-t border-stone-200 space-y-3">
                    <DetailItem label="Why Interested" value={app.why_earn} />
                    <DetailItem label="Why Should Be Selected" value={app.why_selected} />
                    <DetailItem label="Open to Mentorship" value={app.open_to_mentorship} />
                    <DetailItem label="Willing to Commit Time" value={app.commit_time} />
                    <DetailItem label="Follow Structure" value={app.follow_structure} />
                    <DetailItem label="Referred By" value={app.referred_by} />
                    <DetailItem label="Heard From" value={app.heard_from} />
                    <DetailItem label="Application Status" value={app.status} />
                    {/* Notes Field */}
                    <div className="space-y-1 pt-2 border-t border-stone-200">
                      <label className="text-xs font-semibold text-stone-600 uppercase tracking-wider flex items-center gap-1">
                        <i className="ri-sticky-note-line"></i> Internal Notes
                      </label>
                      <textarea
                        value={app.notes || ''}
                        onChange={e =>
                          setApplications(prev =>
                            prev.map(a => (a.id === app.id ? { ...a, notes: e.target.value } : a))
                          )
                        }
                        onBlur={e => updateNotes(app.id, e.target.value)}
                        placeholder="Add private notes or comments about this applicant..."
                        rows={3}
                        className="w-full px-3 py-2 border border-stone-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 text-sm resize-none"
                        disabled={savingNotes === app.id}
                      />
                      {savingNotes === app.id && (
                        <p className="text-xs text-emerald-600 flex items-center gap-1">
                          <div className="w-3 h-3 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin"></div>
                          Saving...
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
        {filteredApplications.length === 0 && (
          <div className="text-center py-12 text-stone-500 bg-white rounded-lg border border-stone-200">
            No applications found matching your search.
          </div>
        )}
      </div>
    </div>
  );
}

function DetailItem({ label, value }: { label: string; value: string }) {
  return (
    <div className="space-y-1">
      <p className="text-xs font-semibold text-stone-500 uppercase tracking-wider">{label}</p>
      <p className="text-sm text-stone-800">{value || '—'}</p>
    </div>
  );
}
